import React, { useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faPlus } from "@fortawesome/pro-regular-svg-icons";
import clsx from "clsx";
import { capitalize } from "../util/stringFormat";
import { equals } from "ramda";
import { useUploadBadge } from "./badgesServices";
import { useSnackAlert } from "../common/use-snackAlert";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import BadgesTable from "./BadgesTable";
import UploadBadgeDialog from "./UploadBadgeDialog";

const useStyles = makeStyles(theme => ({
  iconEnd: {
    transition: "all 0.2s",
    transform: "rotate(0)",
    "& > :first-child": {
      fontSize: "1.5rem",
    },
  },

  iconEndOpen: {
    transform: "rotate(-180deg)",
  },

  header: {
    paddingBottom: 30,
  },

  menuItem: {
    "&:hover": {
      backgroundColor: theme.palette.secondary.light,
      color: "black",
      "& p": {
        color: "black",
      },
    },
  },

  selected: {
    color: theme.palette.primary.main,
    fontWeight: "bolder",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },

  title: {
    fontSize: "2.4rem",
    fontWeight: "bolder",
    color: theme.palette.brandPrimary,
  },
}));

const types = ["all", "award", "black", "white"];

const ManageBadgesBrowse = () => {
  const classes = useStyles();
  const { SnackAlert, showAlert } = useSnackAlert();

  const [typeMenu, setTypeMenu] = useState(false);
  const typeAnchorRef = useRef(null);

  const [type, setType] = useState("all");
  const handleTypeChange = type => event => {
    setType(type);
  };

  //-- Manage Upload Dialog
  const [open, setOpen] = useState(false);
  const openUploadDialog = () => setOpen(true);
  const onCancel = () => setOpen(false);

  const uploadBadge = useUploadBadge();
  const onSave = async (type, file) => {
    try {
      await uploadBadge.mutateAsync({
        resourceIds: { type },
        data: file,
      });

      setOpen(false);
      showAlert("success", ["Badge Uploaded"]);
    } catch (err) {
      console.log(err);
      showAlert("error", [err.message]);
    }
  };

  return (
    <Box height="100%" padding="30px">
      <SnackAlert />
      <UploadBadgeDialog
        visible={open}
        onSave={onSave}
        onCancel={onCancel}
        uploading={uploadBadge.isPending}
      />
      <Grid
        className={classes.header}
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <Typography variant="h1" className={classes.title}>
            Badges
          </Typography>
        </Grid>

        <Grid
          item
          container
          alignItems="center"
          style={{ width: "auto" }}
          spacing={4}
        >
          <Grid item>
            <Button
              onClick={openUploadDialog}
              startIcon={<FontAwesomeIcon icon={faPlus} />}
            >
              Upload Badge
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              ref={typeAnchorRef}
              onClick={() => setTypeMenu(true)}
              style={{
                backgroundColor: typeMenu && "rgba(0, 0, 0, 0.04",
              }}
              classes={{
                iconSizeMedium: clsx(classes.iconEnd, {
                  [classes.iconEndOpen]: typeMenu,
                }),
              }}
              endIcon={<FontAwesomeIcon icon={faChevronDown} />}
            >
              Type
            </Button>
            <Menu
              anchorEl={typeAnchorRef.current}
              open={typeMenu}
              transformOrigin={{
                horizontal: "left",
                vertical: "top",
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              onClose={() => setTypeMenu(false)}
            >
              {types.map((t, idx) => (
                <MenuItem
                  key={t + "-" + idx}
                  classes={{ root: classes.menuItem }}
                  className={clsx({ [classes.selected]: equals(type, t) })}
                  onClick={handleTypeChange(t)}
                >
                  {capitalize(t)}
                </MenuItem>
              ))}
            </Menu>
          </Grid>
        </Grid>
      </Grid>
      <BadgesTable type={type} />
    </Box>
  );
};

export default ManageBadgesBrowse;
